import { useState } from 'react'
import { useRouter } from 'next/router'
import { useQuery } from '@tanstack/react-query'
import { DecagonService } from '@mc/api'
import { isProductionEnv } from '@mc/client-env'
import { useBucket } from '@mc/experiments-react'
import { useUserInfo } from '@mc/user-info'
import { DecagonUserInfo } from '../components/DecagonAi'

const DEFAULT_STALE_TIME = 55 * 60 * 1000 // 55 minutes, BE token expires in 1 hour
const SIGNATURE_STALE_TIME = 210 * 60 * 1000 // 3.5 hours, BE signature expires in 4 hours

const DECAGON_LIVE_CHAT_DISABLED_FLAG_NAME =
  'ce-3517-decagon-live-chat-disabled'

declare global {
  interface Window {
    duet?: {
      open: () => void
      close: () => void
      hide: () => void
      unhide: () => void

      setUserId: (userId: string) => void
      setUserIdAuth: (signature: string, epoch: number) => void
      setMetadata: (
        metadata: Record<string, string | boolean | undefined>,
      ) => void
      setEventListener: (
        callback: (event: { eventName: string }) => void,
      ) => void
      removeEventListener: (
        callback: (event: { eventName: string }) => void,
      ) => void
    }
    decagonEmbed?: {
      setUserId: (userId: string) => void
      setUserIdAuth: (signature: string, epoch: number) => void
      setMetadata: (
        metadata: Record<string, string | boolean | undefined>,
      ) => void
    }
  }
}

export const useDecagon = (userAttributes: DecagonUserInfo, slug: string) => {
  const [isScriptLoaded, setIsScriptLoaded] = useState(false)

  const router = useRouter()
  const liveChatDisabledBucket = useBucket(DECAGON_LIVE_CHAT_DISABLED_FLAG_NAME)

  const handleScriptLoad = () => {
    setIsScriptLoaded(true)
  }

  const { user, isLoading: isLoadingUserInfo } = useUserInfo()

  // USER TOKEN FETCH
  const { data: tokenData, isLoading: isLoadingToken } = useQuery({
    queryKey: ['decagon-token', userAttributes.userId],
    queryFn: () => DecagonService.getUserToken(),
    enabled: userAttributes.is_logged_in,
    staleTime: DEFAULT_STALE_TIME,
    refetchInterval: DEFAULT_STALE_TIME,
  })

  const { userToken: token } = tokenData || {}

  // SIGNATURE FETCH
  const { data: signatureData, isLoading: isLoadingSignature } = useQuery({
    queryKey: ['decagon-signature'],
    queryFn: () => DecagonService.getDecagonSignature(),
    enabled: userAttributes.is_logged_in,
    staleTime: SIGNATURE_STALE_TIME,
    refetchInterval: SIGNATURE_STALE_TIME,
  })

  const { userId: canonicalId, signature, epoch } = signatureData || {}

  const waitingForExtraUserData =
    userAttributes.is_logged_in &&
    (isLoadingToken || isLoadingUserInfo || isLoadingSignature)

  const metadata = {
    // add token check in case session expires and token fetch fails without page reload
    is_logged_in: userAttributes.is_logged_in && !!token,
    platform_name: userAttributes.platform_name,
    device: userAttributes.device,
    country: userAttributes.country,
    region: userAttributes.region,
    current_help_center_product: slug as string, // Legacy
    email: user?.email,
    token,
    sandbox: !isProductionEnv(),
    // widget location:
    widget_page: router.asPath.includes('/chat')
      ? 'decagon landing'
      : 'help center',
    widget_product: slug as string,
    widget_hc_answer: router.asPath.includes('/answer')
      ? `${typeof window !== 'undefined' ? window.location.origin : ''}${router.asPath}`
      : undefined,
    ...(liveChatDisabledBucket === 'on' && { disable_chat: true }),
  }

  return {
    isScriptLoaded,
    handleScriptLoad,
    waitingForExtraUserData,
    userId: canonicalId ?? userAttributes.userId,
    signature,
    epoch,
    metadata,
  }
}
